import { useState, useEffect } from 'react';

import { api } from '@/services';
import { useOAuth } from '@/hooks';

const usePriorConversions = ({
  source,
  target,
  enabled,
  withSourceAccount = false,
}) => {
  const { source: oauthSource, target: oauthTarget } = useOAuth();

  const sourceAuth = oauthSource?.[source];
  const targetAuth = oauthTarget?.[target];

  const sourceAuthId = sourceAuth?.accessToken || sourceAuth?.accountId;
  const targetAuthId = targetAuth?.accessToken || targetAuth?.accountId;

  // Only forward source-account scope when the caller asked for it
  // (Account converter); otherwise the lookup stays platform-scoped.
  const effectiveSourceAuthId = withSourceAccount ? sourceAuthId : null;

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [conversions, setConversions] = useState(null);

  useEffect(() => {
    const missingSource = withSourceAccount && !effectiveSourceAuthId;

    if (!enabled || !source || !target || !targetAuthId || missingSource) {
      setConversions(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    setIsLoading(true);
    setError(null);

    api
      .getPriorConversions({
        source,
        target,
        targetAuthId,
        sourceAuthId: effectiveSourceAuthId,
      })
      .then(({ conversions: result }) => {
        if (cancelled) return;
        setConversions(result);
      })
      .catch((err) => {
        if (cancelled) return;
        setConversions({});
        setError(err);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    source,
    target,
    enabled,
    targetAuthId,
    withSourceAccount,
    effectiveSourceAuthId,
  ]);

  return { error, isLoading, conversions };
};

export default usePriorConversions;
